import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension'
import {
  api,
  confirmCompliancePositions,
  getAssessmentRun,
  getComplianceStaleness,
  startComplianceAssessment,
  type ComplianceStaleness,
} from '@/lib/api'
import { isExtractionStreamingAvailable, streamExtraction } from '@/lib/extraction-client'
import { useBidsStore } from '@/stores/bids-store'
import type {
  ExtractionCategory,
  ExtractionRunSummary,
  ExtractionState,
  ExtractedItem,
  ExtractedComplianceItem,
  CategoryState,
  CriticalityValue,
  PositionValue,
  SweepProgress,
} from '@/types/extraction'

// ===================
// Constants
// ===================

const CATEGORIES: ExtractionCategory[] = [
  'evaluation_criteria',
  'compliance_matrix',
  'submission_checklist',
  'key_dates',
  'risk_register',
]

const UNSEEN_STORAGE_PREFIX = 'bidcontext.analysis.unseen.'

/**
 * A completed key-dates run sets the bid's deadline server-side when the bid
 * has none (the tender close). Pull it into the bids store so the header,
 * Overview, Home and the Bids table show it without a reload. Best-effort.
 */
function syncBidDeadlineAfterRun(bidId: string, category: ExtractionCategory): void {
  if (category !== 'key_dates') return
  void useBidsStore.getState().refreshBidDeadline(bidId)
}

// ===================
// Helpers
// ===================

function createEmptyCategoryState(): CategoryState {
  return {
    state: 'empty',
    items: [],
    streamingItems: [],
    itemCount: 0,
    lastExtractedAt: null,
    newItems: [],
    removedItems: [],
    dismissedTexts: [],
    isReviewingNewItems: false,
    hasUnseenCompletion: false,
    isRecovering: false,
    streamError: null,
    phase: null,
    sweepProgress: null,
    isSweeping: false,
    partialRead: false,
  }
}

function createEmptyCategories(): Record<ExtractionCategory, CategoryState> {
  return {
    evaluation_criteria: createEmptyCategoryState(),
    compliance_matrix: createEmptyCategoryState(),
    submission_checklist: createEmptyCategoryState(),
    key_dates: createEmptyCategoryState(),
    risk_register: createEmptyCategoryState(),
  }
}

/**
 * True once the store is scoped to a *different* bid than `bidId`.
 *
 * Deliberately false when `currentBidId` is null: that means the store is not
 * scoped to a bid yet, which is absence of information rather than a mismatch.
 * Mirrors the same guard in `documents-store.ts`.
 */
function hasMovedToAnotherBid(currentBidId: string | null, bidId: string): boolean {
  return currentBidId !== null && currentBidId !== bidId
}

// Background-recovery tuning. Exported as mutable so tests can shorten them.
// The backend extraction Lambda has a 600s timeout; polling for up to ~11 min
// after a stream failure covers the worst case where the stream drops early
// (e.g. at minute 1) but the Lambda runs to its full 10-min budget before
// persisting items.
export const RECOVERY_POLL_INTERVAL_MS = { value: 10_000 }
export const RECOVERY_POLL_MAX_DURATION_MS = { value: 11 * 60 * 1000 }

// Async sweep watching. A deferred sweep runs on a worker for 20–30 minutes,
// so the client watches the RUN RECORD rather than the stream. 10s is the same
// cadence as recovery: one small request per category being watched, and the
// endpoint doubles as the check-on-read reaper, so polling it is what turns a
// dead worker into a visible failure.
export const SWEEP_WATCH_POLL_INTERVAL_MS = { value: 10_000 }
/**
 * UI budget ONLY. Hitting it stops the poll; the run still completes
 * server-side and the next visit picks the result up from `loadAllCategories`.
 * Generous (90 min) because the worker chain legitimately outlives a single
 * Lambda: the ceiling exists to stop a leaked loop, not to time the run out.
 */
export const SWEEP_WATCH_MAX_DURATION_MS = { value: 90 * 60 * 1000 }

// Drip-feed reveal of items into the streaming UI. Backend dumps all items
// in microseconds at the end of the run (post-process happens server-side
// before any item event), so without throttling the user sees an instant
// dump after a 4-minute wait. Spacing reveals out makes the result feel
// progressively unveiled. Exported for tests to shorten.
export const DRIP_FEED_INTERVAL_MS = { value: 120 }
// Cap the total drip duration so very large batches (e.g. 100 items) don't
// take forever — interval auto-shrinks once a queue exceeds this budget.
const DRIP_FEED_MAX_DURATION_MS = 8_000

function unseenStorageKey(bidId: string): string {
  return `${UNSEEN_STORAGE_PREFIX}${bidId}`
}

function loadUnseenFromStorage(bidId: string): Partial<Record<ExtractionCategory, boolean>> {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(unseenStorageKey(bidId))
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Partial<Record<ExtractionCategory, boolean>>
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function saveUnseenToStorage(
  bidId: string,
  categories: Record<ExtractionCategory, CategoryState>
): void {
  if (typeof window === 'undefined') return
  const unseen: Partial<Record<ExtractionCategory, boolean>> = {}
  for (const cat of CATEGORIES) {
    if (categories[cat].hasUnseenCompletion) unseen[cat] = true
  }
  const key = unseenStorageKey(bidId)
  try {
    if (Object.keys(unseen).length === 0) {
      window.localStorage.removeItem(key)
    } else {
      window.localStorage.setItem(key, JSON.stringify(unseen))
    }
  } catch {
    // localStorage unavailable (private mode, quota) — non-fatal
  }
}

function isManualItem(item: ExtractedItem): boolean {
  return 'extractionSource' in item && item.extractionSource === 'manual'
}

/** Shape of the per-category extraction GET (with `?includeRemoved=true`). */
interface ExtractionCategoryResponse {
  items: ExtractedItem[]
  removedItems?: ExtractedItem[]
  /**
   * Rows Find new items saved `awaiting_review` (mig 194) — the review list.
   * Absent from a backend older than mig 194.
   */
  awaitingItems?: ExtractedItem[]
  dismissedTexts?: string[]
  lastExtractedAt: string | null
}

/**
 * The de-dup / dismissal key for an item: `event` for key_dates, else `title`.
 * (key_dates has no `title`; every other category uses `title`.) Empty string
 * when neither is present so callers can skip it.
 */
function itemMatchText(item: ExtractedItem): string {
  if ('title' in item && typeof item.title === 'string') return item.title
  if ('event' in item && typeof item.event === 'string') return item.event
  return ''
}

/** The saved row id an item carries, or null for an unsaved (streamed-only) copy. */
function savedRowId(item: ExtractedItem): string | null {
  return 'id' in item && typeof item.id === 'string' && item.id !== '' ? item.id : null
}

/**
 * The row is no longer reviewable: deleted (404), or no longer active (409 —
 * superseded by a later run, or already dismissed from another tab). Retrying
 * cannot succeed, so the review list drops it and the table is re-read.
 */
function isReviewTargetGone(error: unknown): boolean {
  const statusCode = (error as { statusCode?: unknown } | null)?.statusCode
  return statusCode === 404 || statusCode === 409
}

/**
 * The accepted row as it should sit in the table. The PATCH answer carries a
 * criterion WITHOUT its children (the server maps the one updated row), so an
 * evaluation criterion keeps the sub-criteria it was reviewed with.
 */
function acceptedRow(serverItem: ExtractedItem, reviewed: ExtractedItem): ExtractedItem {
  if (!('subCriteria' in reviewed) || !Array.isArray(reviewed.subCriteria)) return serverItem
  const serverChildren = 'subCriteria' in serverItem ? serverItem.subCriteria : undefined
  if (Array.isArray(serverChildren) && serverChildren.length > 0) return serverItem
  return { ...serverItem, subCriteria: reviewed.subCriteria } as ExtractedItem
}

/** Put an accepted row in the table — replacing it if a reload already put it there. */
function withAcceptedRows(items: ExtractedItem[], accepted: ExtractedItem[]): ExtractedItem[] {
  let next = items
  for (const row of accepted) {
    const id = savedRowId(row)
    const at = id === null ? -1 : next.findIndex((it) => savedRowId(it) === id)
    next = at === -1 ? [...next, row] : next.map((it, i) => (i === at ? row : it))
  }
  return next
}

/** Drop rows that are waiting for review — they belong in the review list, not the table. */
function withoutPendingRows(items: ExtractedItem[], pending: ExtractedItem[]): ExtractedItem[] {
  const pendingIds = new Set(pending.map(savedRowId).filter((id): id is string => id !== null))
  if (pendingIds.size === 0) return items
  return items.filter((it) => {
    const id = savedRowId(it)
    return id === null || !pendingIds.has(id)
  })
}

/**
 * A Find-new-items row this run left for review, with the streamed copy of it.
 * `awaiting`: saved `awaiting_review` (mig 194). `inserted`: saved active — an
 * older backend, or the new one during the deploy gap before mig 194 runs.
 */
interface FoundRow {
  id: string
  outcome: 'inserted' | 'awaiting'
  streamed: ExtractedItem
}

/**
 * The review list after a Find-new-items run, read from the reloaded table.
 * Rows still awaiting a decision from an earlier run stay first (a second run
 * MATCHES them, so it never reports them as inserted again), then this run's
 * inserts. Every entry is the server row — real id, and for a criterion its
 * saved children — never the streamed copy. Ids the table no longer holds as
 * an active top-level row are dropped.
 */
function reviewRowsFromTable(
  serverItems: ExtractedItem[],
  previous: ExtractedItem[],
  found: FoundRow[]
): ExtractedItem[] {
  const byId = new Map<string, ExtractedItem>()
  for (const it of serverItems) {
    const id = savedRowId(it)
    if (id !== null) byId.set(id, it)
  }
  const ids = [
    ...previous.map(savedRowId).filter((id): id is string => id !== null),
    ...found.map((f) => f.id),
  ]
  const rows: ExtractedItem[] = []
  const seen = new Set<string>()
  for (const id of ids) {
    if (seen.has(id)) continue
    seen.add(id)
    const row = byId.get(id)
    if (row) rows.push(row)
  }
  return rows
}

/**
 * The review list and table a category GET implies.
 *
 * A backend on mig 194 returns the review list itself (`awaitingItems`), so
 * it is authoritative: rows another tab already accepted or dismissed drop
 * out, and the list survives a reload. Order is kept stable for the user —
 * rows already on screen first (in their current order), then any others the
 * server holds, then rows this run saved ACTIVE (the pre-194 deploy-gap
 * fallback reports them `inserted`), which are held out of the table for
 * review exactly as before.
 *
 * An older backend has no `awaitingItems`: the review list is rebuilt from
 * the active rows this session remembers (`reviewRowsFromTable`).
 *
 * The table never contains a row that is in the review list.
 */
function reviewListFromResponse(
  data: ExtractionCategoryResponse,
  current: ExtractedItem[],
  found: FoundRow[]
): { newItems: ExtractedItem[]; items: ExtractedItem[] } {
  if (!Array.isArray(data.awaitingItems)) {
    const newItems = reviewRowsFromTable(data.items, current, found)
    return { newItems, items: withoutPendingRows(data.items, newItems) }
  }

  const awaitingById = new Map<string, ExtractedItem>()
  for (const it of data.awaitingItems) {
    const id = savedRowId(it)
    if (id !== null) awaitingById.set(id, it)
  }
  const activeById = new Map<string, ExtractedItem>()
  for (const it of data.items) {
    const id = savedRowId(it)
    if (id !== null) activeById.set(id, it)
  }

  const newItems: ExtractedItem[] = []
  const seen = new Set<string>()
  const add = (id: string | null, row: ExtractedItem | undefined) => {
    if (id === null || row === undefined || seen.has(id)) return
    seen.add(id)
    newItems.push(row)
  }
  for (const it of current) {
    const id = savedRowId(it)
    if (id !== null) add(id, awaitingById.get(id))
  }
  for (const it of data.awaitingItems) add(savedRowId(it), it)
  // Only a row saved ACTIVE is held from the table. A row saved awaiting that
  // is no longer awaiting was decided elsewhere (accepted in another tab, or
  // superseded) and follows the server.
  for (const f of found) {
    if (f.outcome === 'inserted') add(f.id, activeById.get(f.id))
  }

  return { newItems, items: withoutPendingRows(data.items, newItems) }
}

/**
 * The review list and table for an ordinary category read (no Find run just
 * finished). With `awaitingItems` the server's list wins — this is what
 * restores the review list after a reload. Without it (older backend) the
 * session's list is kept as it was and only held out of the table.
 */
function reviewListForLoad(
  data: ExtractionCategoryResponse,
  current: ExtractedItem[]
): { newItems: ExtractedItem[]; items: ExtractedItem[] } {
  if (Array.isArray(data.awaitingItems)) return reviewListFromResponse(data, current, [])
  return { newItems: current, items: withoutPendingRows(data.items, current) }
}

/** Fallback review list when the reload failed: the streamed copies, stamped with their saved ids. */
function reviewRowsFromStream(previous: ExtractedItem[], found: FoundRow[]): ExtractedItem[] {
  const rows = [...previous]
  const seen = new Set(previous.map(savedRowId).filter((id): id is string => id !== null))
  for (const f of found) {
    if (seen.has(f.id)) continue
    seen.add(f.id)
    rows.push({ ...f.streamed, id: f.id } as ExtractedItem)
  }
  return rows
}

type ReviewDecision = 'accept' | 'dismiss'

type ReviewResult =
  | { kind: 'done'; serverItem: ExtractedItem }
  /** Nothing left to decide on: no saved id, or the row is deleted / no longer active. */
  | { kind: 'gone' }
  | { kind: 'failed'; error: unknown }

/**
 * Accept or dismiss the row Find new items already SAVED, by its id. Never
 * POSTs `/manual`: that inserted a second copy of the row (accept) or a
 * tombstone beside the still-active one (dismiss). An item with no saved id
 * cannot be addressed, so it is reported gone rather than re-created.
 */
async function reviewSavedRow(
  bidId: string,
  category: ExtractionCategory,
  item: ExtractedItem,
  decision: ReviewDecision
): Promise<ReviewResult> {
  const id = savedRowId(item)
  if (id === null) return { kind: 'gone' }
  try {
    const { item: serverItem } = await api.patch<{ item: ExtractedItem }>(
      `/bids/${bidId}/extraction/${category}/${id}`,
      { review: decision }
    )
    return { kind: 'done', serverItem }
  } catch (error) {
    return isReviewTargetGone(error) ? { kind: 'gone' } : { kind: 'failed', error }
  }
}

/**
 * Stale-detection selector (pure). A populated category is stale when its
 * `lastExtractedAt` predates the newest bid document — the extraction no longer
 * reflects the uploaded set. Never-extracted categories (not populated, or no
 * `lastExtractedAt`) are excluded. Per-category, not per-bid. Exported so the
 * component can wire the extraction + documents stores together.
 */
export function computeStaleCategories(
  categories: Record<ExtractionCategory, CategoryState>,
  documentCreatedAts: string[]
): ExtractionCategory[] {
  if (documentCreatedAts.length === 0) return []
  const newest = documentCreatedAts.reduce((a, b) => (a > b ? a : b))
  const stale: ExtractionCategory[] = []
  for (const cat of CATEGORIES) {
    const c = categories[cat]
    if (c.state !== 'populated') continue
    if (!c.lastExtractedAt) continue
    if (c.lastExtractedAt < newest) stale.push(cat)
  }
  return stale
}

/**
 * True when a successful extraction run has completed for this category — even
 * if it returned zero items. Distinguishes "ran, found nothing" (done) from
 * "never run" (pending).
 *
 * `lastExtractedAt` is the latest COMPLETED run's timestamp (see
 * bid-analysis.ts getLastExtractedAt), so a failed run does not satisfy it.
 */
export function hasCompletedRun(category: CategoryState): boolean {
  return category.lastExtractedAt !== null
}

/**
 * Ran to completion and returned nothing — the category is DONE, not pending.
 * Surfaces must not offer to re-run it by default, and must not claim the user
 * still has work to do.
 *
 * The test is for a SETTLED category, not for `state === 'empty'`. "This pack
 * defines none" is a claim about a finished run, so it may only be made when
 * nothing is in flight (not extracting, not recovering) and nothing errored —
 * otherwise we assert a conclusion while the app is still finding out:
 *
 * - `isRecovering` — a stream failure sets `state: 'empty'` but leaves `items`
 *   and `lastExtractedAt` from the PREVIOUS run intact, then polls in the
 *   background. Without this clause a re-run that is still recovering reads as
 *   "done, found nothing".
 * - `streamError` — a failed RE-run keeps the earlier successful run's
 *   timestamp, so `hasCompletedRun` is true while an error banner is showing.
 * - `items.length` — same stream-failure path can leave a populated `items`
 *   array sitting under `state: 'empty'`, so state alone is not enough.
 *
 * `state !== 'extracting'` rather than `state === 'empty'` because a zero-item
 * run is finalized as `populated` with `items: []` (see `finalizeRun`), and it
 * only self-corrects if the follow-up `loadCategory` succeeds — that reload
 * swallows its failures, so the category can legitimately sit at
 * `populated` + `items: []` for the rest of the session.
 */
export function isRanAndEmpty(category: CategoryState): boolean {
  return (
    category.state !== 'extracting' &&
    !category.isRecovering &&
    category.streamError === null &&
    category.items.length === 0 &&
    hasCompletedRun(category)
  )
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}

// ===================
// Drip-feed manager
// ===================
//
// Per-category queue of pending items + a "complete" total awaiting drain.
// We can't keep this in Zustand state because we update it on every tick
// inside a setInterval — keeping it in module scope avoids re-render
// thrash. The store is updated only on commits (one item per tick, plus
// the final populated commit when the queue drains).

interface DripQueue {
  items: ExtractedItem[]
  /** Set when onComplete fires — total items the run will have, including
   *  whatever's still queued. Drives the finalize commit when items run out. */
  completeTotal: number | null
  bidId: string
  intervalId: ReturnType<typeof setInterval> | null
}

const dripQueues: Partial<Record<ExtractionCategory, DripQueue>> = {}

function clearDripQueue(category: ExtractionCategory): void {
  const q = dripQueues[category]
  if (q?.intervalId) clearInterval(q.intervalId)
  delete dripQueues[category]
}

type StoreSet = (
  updater: (state: ExtractionStoreState) => Partial<ExtractionStoreState>
) => void
type StoreGet = () => ExtractionStoreState

/**
 * Apply the outcome of one or more Find-new-items review decisions.
 *
 * - done: accepted rows join the table (once, by id); dismissed rows record
 *   their text. Either way they leave the review list.
 * - gone: the row cannot be decided on any more, so it leaves the review list
 *   and the table is re-read, so the screen shows what the server holds.
 * - failed: the item stays in the review list so the user can retry.
 */
function settleReviews(
  set: StoreSet,
  get: StoreGet,
  bidId: string,
  category: ExtractionCategory,
  decision: ReviewDecision,
  results: Array<{ item: ExtractedItem; result: ReviewResult }>
): void {
  if (hasMovedToAnotherBid(get().currentBidId, bidId)) return

  const resolved = new Set<ExtractedItem>()
  const reviewed: Array<{ item: ExtractedItem; serverItem: ExtractedItem }> = []
  let failedCount = 0
  let anyGone = false
  for (const { item, result } of results) {
    if (result.kind === 'failed') {
      failedCount += 1
      console.error(`Failed to ${decision} new ${category} item:`, result.error)
      continue
    }
    resolved.add(item)
    if (result.kind === 'gone') anyGone = true
    else reviewed.push({ item, serverItem: result.serverItem })
  }
  if (failedCount > 0 && results.length > 1) {
    console.error(`Failed to ${decision} ${failedCount}/${results.length} new ${category} items`)
  }
  if (resolved.size === 0) return

  set((state) => {
    const cat = state.categories[category]
    const newItems = cat.newItems.filter((it) => !resolved.has(it))
    if (decision === 'accept') {
      const items = withAcceptedRows(
        cat.items,
        reviewed.map(({ item, serverItem }) => acceptedRow(serverItem, item))
      )
      return {
        categories: {
          ...state.categories,
          [category]: {
            ...cat,
            items,
            itemCount: items.length,
            newItems,
            isReviewingNewItems: newItems.length > 0,
          },
        },
      }
    }
    const texts = reviewed.map(({ item }) => itemMatchText(item)).filter((t) => t !== '')
    return {
      categories: {
        ...state.categories,
        [category]: {
          ...cat,
          newItems,
          isReviewingNewItems: newItems.length > 0,
          dismissedTexts: texts.length > 0 ? [...cat.dismissedTexts, ...texts] : cat.dismissedTexts,
        },
      },
    }
  })

  if (anyGone) void get().loadCategory(bidId, category)
}

/** The category's table after a Find-new-items run, or null when it could not be read. */
async function readTableAfterFind(
  bidId: string,
  category: ExtractionCategory
): Promise<ExtractionCategoryResponse | null> {
  try {
    const response = await api.get<ExtractionCategoryResponse>(
      `/bids/${bidId}/extraction/${category}?includeRemoved=true`
    )
    if (Array.isArray(response?.items)) return response
    console.error(`Find new items reload for ${category} returned no item list`)
  } catch (error) {
    console.error(`Find new items reload failed for ${category}:`, error)
  }
  return null
}

/**
 * After a Find-new-items run: re-read the category and rebuild the review list.
 * Since mig 194 the rows this run found are saved `awaiting_review`, and the GET
 * returns them as `awaitingItems` — never in the table. Rows saved active (an
 * older backend, or the deploy gap before mig 194) come back in the table and
 * are held out of it for review instead. See `reviewListFromResponse`.
 *
 * If the re-read fails, the review list falls back to the streamed copies
 * stamped with their saved ids (still addressable by id), and the table is
 * left as it was.
 */
async function reconcileFoundRows(
  set: StoreSet,
  bidId: string,
  category: ExtractionCategory,
  found: FoundRow[]
): Promise<void> {
  const data = await readTableAfterFind(bidId, category)

  set((state) => {
    // The user moved to another bid, or a full extraction took over this
    // category while the GET was in flight — this result no longer applies.
    if (hasMovedToAnotherBid(state.currentBidId, bidId)) return state
    const cat = state.categories[category]
    if (cat.state === 'extracting') return state

    if (data === null) {
      const newItems = reviewRowsFromStream(cat.newItems, found)
      return {
        categories: {
          ...state.categories,
          [category]: { ...cat, newItems, isReviewingNewItems: newItems.length > 0 },
        },
      }
    }

    const { newItems, items } = reviewListFromResponse(data, cat.newItems, found)
    // A category whose only rows await review is populated, not empty — the
    // review banner lives in the populated view.
    const hasRows = items.length + newItems.length > 0
    return {
      categories: {
        ...state.categories,
        [category]: {
          ...cat,
          state: hasRows ? ('populated' as ExtractionState) : ('empty' as ExtractionState),
          items,
          itemCount: items.length,
          removedItems: Array.isArray(data.removedItems) ? data.removedItems : [],
          dismissedTexts: Array.isArray(data.dismissedTexts) ? data.dismissedTexts : [],
          lastExtractedAt: data.lastExtractedAt ?? null,
          ...(hasRows ? { streamError: null, isRecovering: false } : {}),
          newItems,
          isReviewingNewItems: newItems.length > 0,
        },
      },
    }
  })
}

/**
 * Push an item into the per-category drip queue and start (or update) the
 * release timer. Each tick releases the head of the queue into
 * `streamingItems`. When the queue empties and `completeTotal` is set,
 * the run is finalized into the populated state.
 */
function enqueueDripItem(
  bidId: string,
  category: ExtractionCategory,
  item: ExtractedItem,
  set: StoreSet,
  get: StoreGet
): void {
  let queue = dripQueues[category]
  if (!queue || queue.bidId !== bidId) {
    // New run for this category — wipe any stale queue and start fresh.
    if (queue) clearDripQueue(category)
    queue = { items: [], completeTotal: null, bidId, intervalId: null }
    dripQueues[category] = queue
  }
  queue.items.push(item)

  // Lazy-start the interval. The interval lives until the queue drains.
  if (queue.intervalId === null) {
    const tick = () => {
      const q = dripQueues[category]
      if (!q || q.bidId !== bidId) {
        // Cancelled or superseded.
        if (q?.intervalId) clearInterval(q.intervalId)
        return
      }
      // Bail if the user navigated away mid-drip.
      if (get().currentBidId !== bidId) {
        clearDripQueue(category)
        return
      }
      const next = q.items.shift()
      if (next !== undefined) {
        set((state) => ({
          categories: {
            ...state.categories,
            [category]: {
              ...state.categories[category],
              streamingItems: [...state.categories[category].streamingItems, next],
            },
          },
        }))
      }
      // If we've drained AND complete already arrived, finalize.
      if (q.items.length === 0 && q.completeTotal !== null) {
        const total = q.completeTotal
        clearDripQueue(category)
        finalizeRun(category, total, set, get)
      }
    }

    // Auto-shrink the interval if a very large batch landed (cap total drip
    // duration at DRIP_FEED_MAX_DURATION_MS so 100+ items don't take >10s).
    const baseInterval = DRIP_FEED_INTERVAL_MS.value
    const projectedDuration = queue.items.length * baseInterval
    const interval =
      projectedDuration > DRIP_FEED_MAX_DURATION_MS
        ? Math.max(20, Math.floor(DRIP_FEED_MAX_DURATION_MS / queue.items.length))
        : baseInterval
    queue.intervalId = setInterval(tick, interval)
  }
}

/**
 * Commit the final populated state for a run. Merges streamed items with any
 * existing manual items. Always called once per run, either after the drip
 * drains or immediately for reduced-motion / zero-item completions.
 */
function finalizeRun(
  category: ExtractionCategory,
  total: number,
  set: StoreSet,
  get: StoreGet
): void {
  set((state) => {
    const cat = state.categories[category]
    const manualItems = cat.items.filter(isManualItem)
    const allItems = [...cat.streamingItems, ...manualItems]
    const nextCategories = {
      ...state.categories,
      [category]: {
        ...cat,
        state: 'populated' as ExtractionState,
        items: allItems,
        streamingItems: [],
        itemCount: total + manualItems.length,
        lastExtractedAt: new Date().toISOString(),
        hasUnseenCompletion: true,
        phase: null,
      },
    }
    if (state.currentBidId) {
      saveUnseenToStorage(state.currentBidId, nextCategories)
    }
    return { categories: nextCategories }
  })
  // Best-effort: keep get() warm so type inference doesn't grump
  void get
}

/** Find the latest run for a category from a list returned by /extraction-runs. */
function latestRunForCategory(
  runs: ExtractionRunSummary[],
  category: ExtractionCategory
): ExtractionRunSummary | null {
  // Runs come back newest-first (ORDER BY created_at DESC in the repo), so the
  // first match wins. Be defensive in case ordering ever changes.
  let best: ExtractionRunSummary | null = null
  for (const run of runs) {
    if (run.category !== category) continue
    if (!best || run.createdAt > best.createdAt) best = run
  }
  return best
}

/**
 * After a stream failure — or on page reload while a run is still in flight —
 * the Lambda often finishes persisting items a minute or two later. This polls
 * BOTH the items endpoint (for completion) and the runs endpoint (to refresh
 * the stepper phase and detect terminal failures) until one of those fires or
 * the budget runs out. Bails early if a new extraction supersedes us or the
 * user switches bids.
 */
function startBackgroundRecovery(
  bidId: string,
  category: ExtractionCategory,
  errorMessage: string,
  get: () => ExtractionStoreState,
  set: (updater: (state: ExtractionStoreState) => Partial<ExtractionStoreState>) => void
): void {
  // Mark the category as recovering so the UI can show a banner.
  set((state) => ({
    categories: {
      ...state.categories,
      [category]: {
        ...state.categories[category],
        isRecovering: true,
        streamError: null,
      },
    },
  }))

  void (async () => {
    const startedAt = Date.now()
    while (Date.now() - startedAt < RECOVERY_POLL_MAX_DURATION_MS.value) {
      await new Promise((resolve) => setTimeout(resolve, RECOVERY_POLL_INTERVAL_MS.value))

      // Bail if we've been superseded or the user navigated away.
      const current = get().categories[category]
      if (!current.isRecovering || get().currentBidId !== bidId) return
      if (current.state === 'extracting' || current.state === 'populated') return

      // Refresh the run summary first — gives us live phase + terminal status.
      // Best-effort: a single tick failure shouldn't stop polling.
      let latestRun: ExtractionRunSummary | null = null
      try {
        const runs = await api.get<ExtractionRunSummary[]>(`/bids/${bidId}/extraction-runs`)
        latestRun = Array.isArray(runs) ? latestRunForCategory(runs, category) : null
      } catch {
        // Network flake — fall through to the items poll.
      }

      // Update phase on every tick so the stepper visibly advances. We do this
      // even if the run completed; the items poll below will finalize state.
      if (latestRun && latestRun.phase) {
        const nextPhase = latestRun.phase
        const beforePhase = get().categories[category]
        if (beforePhase.isRecovering && beforePhase.phase !== nextPhase) {
          set((state) => ({
            categories: {
              ...state.categories,
              [category]: {
                ...state.categories[category],
                phase: nextPhase,
              },
            },
          }))
        }
      }

      // Terminal failure with no items — surface the error and stop polling.
      if (latestRun?.status === 'failed') {
        const failureMessage = latestRun.errorMessage ?? errorMessage
        const after = get().categories[category]
        if (after.isRecovering && get().currentBidId === bidId) {
          set((state) => ({
            categories: {
              ...state.categories,
              [category]: {
                ...state.categories[category],
                isRecovering: false,
                streamError: failureMessage,
                phase: null,
              },
            },
          }))
        }
        return
      }

      try {
        const data = await api.get<ExtractionCategoryResponse>(
          `/bids/${bidId}/extraction/${category}?includeRemoved=true`
        )
        const items = Array.isArray(data?.items) ? data.items : []
        if (items.length === 0) continue
        const removedItems = Array.isArray(data?.removedItems) ? data.removedItems : []
        const dismissedTexts = Array.isArray(data?.dismissedTexts) ? data.dismissedTexts : []

        // Re-check that we're still the active recovery before mutating.
        const after = get().categories[category]
        if (!after.isRecovering || get().currentBidId !== bidId) return
        if (after.state === 'extracting') return

        set((state) => {
          // Rows in the Find-new-items review list stay out of the table.
          const { newItems, items: tableItems } = reviewListForLoad(
            { ...data, items },
            state.categories[category].newItems
          )
          return {
            categories: {
              ...state.categories,
              [category]: {
                ...state.categories[category],
                state: 'populated' as ExtractionState,
                items: tableItems,
                newItems,
                isReviewingNewItems: newItems.length > 0,
                removedItems,
                dismissedTexts,
                itemCount: tableItems.length,
                lastExtractedAt: data?.lastExtractedAt ?? null,
                isRecovering: false,
                streamError: null,
                hasUnseenCompletion: true,
                phase: null,
              },
            },
          }
        })
        if (get().currentBidId) {
          saveUnseenToStorage(get().currentBidId!, get().categories)
        }
        syncBidDeadlineAfterRun(bidId, category)
        return
      } catch {
        // Network flake — keep polling.
      }
    }

    // Timed out. Leave items as-is and surface the failure so the user can retry.
    const finalState = get().categories[category]
    if (finalState.isRecovering && get().currentBidId === bidId) {
      set((state) => ({
        categories: {
          ...state.categories,
          [category]: {
            ...state.categories[category],
            isRecovering: false,
            streamError: errorMessage,
            phase: null,
          },
        },
      }))
    }
  })()
}

// ===================
// Async sweep watcher
// ===================
//
// Categories that can be dispatched to the async sweep worker. Everything else
// runs inline and is covered by `startBackgroundRecovery`.
const SWEEP_CATEGORIES: readonly ExtractionCategory[] = [
  'compliance_matrix',
  'submission_checklist',
]

/**
 * Which run each category is currently being watched for. Module scope, like
 * `dripQueues`: a newer runId supersedes the older loop (it exits on its next
 * tick), and deleting the entry ends the loop outright.
 */
const sweepWatchers: Partial<Record<ExtractionCategory, string>> = {}

/** True for a run that belongs to the sweep watcher rather than inline recovery. */
function isSweepRun(run: ExtractionRunSummary, category: ExtractionCategory): boolean {
  return SWEEP_CATEGORIES.includes(category) && (run.heartbeatAt ?? null) !== null
}

/**
 * Watch a deferred sweep run to its terminal status.
 *
 * This is NOT `startBackgroundRecovery`. Recovery was built for "the stream
 * died, items land within minutes" and finalises the moment the items endpoint
 * returns anything — which, on a re-run over an existing matrix, is the
 * PREVIOUS run's rows on the first tick. A sweep runs for 20–30 minutes on top
 * of those rows, so the only trustworthy completion signal is the run status
 * flipping off `running`. This loop therefore reads the run record and nothing
 * else, and only touches items once the server says the run is over.
 *
 * Ends on: terminal status, a newer run superseding this one, the entry being
 * deleted (cancel / reset), the user switching bids, or the UI budget.
 */
function watchSweepRun(
  bidId: string,
  category: ExtractionCategory,
  runId: string,
  get: StoreGet,
  set: StoreSet
): void {
  if (sweepWatchers[category] === runId) return // already watching this run
  sweepWatchers[category] = runId

  // Mark the category as swept the instant the watcher takes ownership — this
  // is the single choke point every entry path goes through, so the UI can
  // promise "you can leave this page" from queue time rather than waiting up
  // to a full poll interval for the first window count.
  set((state) => ({
    categories: {
      ...state.categories,
      [category]: { ...state.categories[category], isSweeping: true },
    },
  }))

  void (async () => {
    const startedAt = Date.now()
    while (Date.now() - startedAt < SWEEP_WATCH_MAX_DURATION_MS.value) {
      await new Promise((resolve) => setTimeout(resolve, SWEEP_WATCH_POLL_INTERVAL_MS.value))

      // Superseded or cancelled: a NEWER runId (or a deliberate cancel) owns
      // the registry entry now — leave it alone, it isn't ours to clear.
      if (sweepWatchers[category] !== runId) return
      // Navigated to another bid. The entry MUST be dropped on the way out:
      // nothing clears it on SPA navigation, and a stale entry would make the
      // idempotence guard above refuse to start a fresh loop when the user
      // comes back — a stepper that spins forever with nobody polling.
      if (get().currentBidId !== bidId) {
        delete sweepWatchers[category]
        return
      }

      let run: ExtractionRunSummary | null = null
      try {
        const runs = await api.get<ExtractionRunSummary[]>(`/bids/${bidId}/extraction-runs`)
        run = Array.isArray(runs) ? runs.find((r) => r.id === runId) ?? null : null
      } catch {
        // Network flake — a dropped poll is not a dead run. Keep polling.
        continue
      }
      if (!run) continue

      // Re-check after the await — same two exits, same rule about the entry.
      if (sweepWatchers[category] !== runId) return
      if (get().currentBidId !== bidId) {
        delete sweepWatchers[category]
        return
      }

      if (run.status === 'running' || run.status === 'pending') {
        const nextPhase = run.phase ?? null
        set((state) => ({
          categories: {
            ...state.categories,
            [category]: {
              ...state.categories[category],
              phase: nextPhase,
              // KEEP the last known count when this poll came back without
              // one. The progress read can fail server-side independently of
              // the run (the handler returns the run with no `sweepProgress`
              // rather than failing the request), and dropping to null would
              // blink the counter off and shrink the card for a cycle. The
              // terminal branches below null it, so it cannot go stale.
              sweepProgress: run.sweepProgress ?? state.categories[category].sweepProgress,
              isSweeping: true,
            },
          },
        }))
        continue
      }

      // Terminal.
      delete sweepWatchers[category]

      if (run.status === 'completed') {
        // Finalize assesses the new rows by itself once the matrix is written.
        // Not awaited — the adoption owns its own poll, and awaiting it here
        // would open a gap before the state change below.
        if (category === 'compliance_matrix') {
          void adoptServerStartedAssessment(bidId, get, set)
        }
        // Clear `extracting` FIRST — `loadCategory` refuses to touch a category
        // that is still extracting, so reloading before this would be a no-op
        // and the category would sit on the stepper forever. A zero-item
        // completion is legitimate and must settle to empty, not hang.
        set((state) => ({
          categories: {
            ...state.categories,
            [category]: {
              ...state.categories[category],
              state: 'empty' as ExtractionState,
              streamingItems: [],
              isRecovering: false,
              streamError: null,
              phase: null,
              sweepProgress: null,
              isSweeping: false,
              // A window could not be read in full, so the run kept the rows
              // it did not reach. The table says so.
              partialRead: run.partialRead === true,
            },
          },
        }))
        // The run record is the completion signal; the rows come from REST
        // (real ids, superseded rows, merged manual items).
        await get().loadCategory(bidId, category)
        if (get().currentBidId !== bidId) return
        set((state) => {
          const nextCategories = {
            ...state.categories,
            [category]: {
              ...state.categories[category],
              hasUnseenCompletion: true,
            },
          }
          saveUnseenToStorage(bidId, nextCategories)
          return { categories: nextCategories }
        })
        return
      }

      // Failed (including a run the reaper killed for heartbeat silence).
      // The previous matrix is deliberately left untouched — nothing new was
      // persisted, so there is nothing to reload and plenty to lose. A category
      // that still HAS rows also stays `populated`: dropping it to `empty`
      // would hide the customer's existing work behind a full-panel error, and
      // a failed re-run doesn't unmake the run that produced those rows. The
      // failure rides along as `streamError`, which the table shows as a banner
      // above the rows (and as the error view when there are none).
      const failureMessage = run.errorMessage ?? 'Analysis failed. Please try again.'
      set((state) => {
        const cat = state.categories[category]
        return {
          categories: {
            ...state.categories,
            [category]: {
              ...cat,
              state: (cat.items.length > 0 ? 'populated' : 'empty') as ExtractionState,
              streamingItems: [],
              isRecovering: false,
              streamError: failureMessage,
              phase: null,
              sweepProgress: null,
              isSweeping: false,
            },
          },
        }
      })
      return
    }

    // Budget spent. Stop polling and leave the category as it is — the run
    // finishes server-side and the next load picks it up.
    if (sweepWatchers[category] === runId) delete sweepWatchers[category]
  })()
}

/**
 * Put a category under the sweep watcher from the RUN RECORD alone, for the
 * cases where no `extraction:queued` event reached us:
 *
 * - the event arrived without a `runId` (malformed / older backend), and
 * - the stream dropped in the window between the server dispatching the sweep
 *   and the event being written.
 *
 * Both used to land in `startBackgroundRecovery`, which finalises on
 * items-arrived — on a re-run that is the PREVIOUS run's rows, i.e. exactly the
 * false completion this whole task exists to prevent.
 *
 * Resolves true when a live sweep run was found and adopted.
 */
async function tryAttachRunningSweep(
  bidId: string,
  category: ExtractionCategory,
  get: StoreGet,
  set: StoreSet
): Promise<boolean> {
  if (!SWEEP_CATEGORIES.includes(category)) return false
  let latest: ExtractionRunSummary | null = null
  try {
    const runs = await api.get<ExtractionRunSummary[]>(`/bids/${bidId}/extraction-runs`)
    latest = Array.isArray(runs) ? latestRunForCategory(runs, category) : null
  } catch {
    return false
  }
  if (!latest || latest.status !== 'running' || !isSweepRun(latest, category)) return false
  if (get().currentBidId !== bidId) return false

  const sweepPhase = latest.phase ?? null
  const sweepProgress: SweepProgress | null = latest.sweepProgress ?? null
  set((state) => ({
    categories: {
      ...state.categories,
      [category]: {
        ...state.categories[category],
        state: 'extracting' as ExtractionState,
        phase: sweepPhase,
        sweepProgress,
        isRecovering: false,
        streamError: null,
      },
    },
  }))
  watchSweepRun(bidId, category, latest.id, get, set)
  return true
}

function hasId(item: ExtractedItem, id: string): boolean {
  return 'id' in item && item.id === id
}

// ===================
// Compliance assessment run
// ===================

/**
 * Lifecycle of the AI position assessment for the current bid. `idle` means
 * nothing has been triggered this session — NOT that the bid has never been
 * assessed (that reads off the items' `positionAssessedAt`).
 */
export interface AssessmentRunState {
  status: 'idle' | 'running' | 'completed' | 'failed'
  runId: string | null
  bidId: string | null
  errorMessage: string | null
  /** Rows scored by the finished run. Null until it completes. */
  itemCount: number | null
  /** True only when this client's `startAssessment` started the run; false for runs adopted from the server (post-extraction adoption, mount-time resume). */
  triggeredByUser: boolean
}

export function createEmptyAssessmentRun(): AssessmentRunState {
  return {
    status: 'idle',
    runId: null,
    bidId: null,
    errorMessage: null,
    itemCount: null,
    triggeredByUser: false,
  }
}

// Poll cadence while an assessment is in flight. Exported mutable so tests can
// shorten them, matching the recovery-poll convention above.
export const ASSESSMENT_POLL_INTERVAL_MS = { value: 5_000 }
/**
 * Hard stop on polling. The trigger is a fire-and-forget Lambda invoke, so a
 * run row may never appear at all (kill switch off, nothing eligible, invoke
 * lost). Without a ceiling the UI would sit on "running" forever and the
 * interval would leak for the life of the tab.
 */
export const ASSESSMENT_POLL_MAX_DURATION_MS = { value: 15 * 60 * 1000 }
/**
 * How long `adoptServerStartedAssessment` waits for the server's run row to
 * appear before standing down quietly. The row is normally there within a
 * second or two of the extraction finishing (prod: 1s); the slack covers a
 * cold start or a briefly throttled invoke. Kept far below the poll ceiling
 * because this is the one case where "no row" is a legitimate answer.
 */
export const ASSESSMENT_ADOPTION_WINDOW_MS = { value: 45_000 }

let assessmentPollTimer: ReturnType<typeof setInterval> | null = null

/**
 * Bumped by every stop. `startAssessment` captures it before its awaits and
 * refuses to arm if it has moved — without that, a stop (or a second trigger)
 * landing during the probe/POST window is silently undone by the interval
 * arming afterwards, and the orphaned timer is unreachable forever.
 */
let assessmentPollGeneration = 0

/** Idempotent — safe on an already-stopped poll, and safe to call twice. */
function stopAssessmentPoll(): void {
  assessmentPollGeneration++
  if (assessmentPollTimer !== null) {
    clearInterval(assessmentPollTimer)
    assessmentPollTimer = null
  }
}

/**
 * Arm the interval that watches one assessment run through to its terminal
 * state, reloading the matrix when it completes.
 *
 * Shared by `startAssessment`, which has just triggered a run,
 * `resumeAssessmentPolling`, which adopted one that was already in flight, and
 * `adoptServerStartedAssessment`, which waits on a run the server starts by
 * itself. The tick is identical for all three; only `ignoreRunId` and
 * `adoptWithinMs` differ — a fresh trigger has a stale pre-trigger run it must
 * not mistake for its own, an adopted run does not, and only a run nobody here
 * asked for may legitimately never appear.
 *
 * The caller MUST call `stopAssessmentPoll()` and capture the resulting
 * `assessmentPollGeneration` BEFORE its own awaits, and pass it here: this
 * refuses to arm if the generation has moved, so a stop or a newer trigger
 * landing mid-await is never silently undone by an interval armed afterwards.
 */
function armAssessmentPoll({
  get,
  set,
  bidId,
  generation,
  ignoreRunId,
  adoptWithinMs,
}: {
  get: () => ExtractionStoreState
  set: (partial: Partial<ExtractionStoreState>) => void
  bidId: string
  generation: number
  /** Newest run from before the trigger, to be ignored. Null when adopting. */
  ignoreRunId: string | null
  /**
   * Stand down quietly — back to `idle`, no error — if no run other than
   * `ignoreRunId` has appeared within this many ms. Only for a run this client
   * did not trigger; see `adoptServerStartedAssessment`.
   */
  adoptWithinMs?: number
}): void {
  const deadline = Date.now() + ASSESSMENT_POLL_MAX_DURATION_MS.value
  const adoptBy = adoptWithinMs === undefined ? null : Date.now() + adoptWithinMs
  // Set once the run being waited for has shown up; the adoption window no
  // longer applies after that.
  let found = false

  /**
   * Whether this poll may still touch shared state. Checked on BOTH sides of
   * the request: a stop, a newer trigger or a bid switch can land while it is
   * in flight, and writing the response anyway would put back a `running`
   * status nothing polls (which the next mount's resume reads as "already
   * watching" — a spinner for good), or reload one bid's rows into the store
   * while it is scoped to another.
   */
  const ownsPoll = (): boolean => {
    // Superseded by a stop or a newer trigger — never touch shared state.
    if (assessmentPollGeneration !== generation) return false
    // Bail if the user moved on.
    if (get().assessmentRun.bidId !== bidId) {
      stopAssessmentPoll()
      return false
    }
    // The store now belongs to another bid. In practice only a poll that no
    // mounted matrix view owns gets here (a view's unmount stops its own) —
    // i.e. a post-extraction adoption. Clear the status too: left at `running`
    // it would hold the bid-agnostic re-entrancy guard shut on the new bid.
    if (hasMovedToAnotherBid(get().currentBidId, bidId)) {
      stopAssessmentPoll()
      set({ assessmentRun: createEmptyAssessmentRun() })
      return false
    }
    return true
  }

  const tick = async (): Promise<void> => {
    if (!ownsPoll()) return

    // The run being waited for never appeared: the server legitimately started
    // nothing (kill switch, lost invoke). Nobody asked for it, so there is no
    // failure to report — back to idle, and the Assess button is still there.
    if (adoptBy !== null && !found && Date.now() > adoptBy) {
      stopAssessmentPoll()
      set({ assessmentRun: createEmptyAssessmentRun() })
      return
    }

    if (Date.now() > deadline) {
      stopAssessmentPoll()
      set({
        assessmentRun: {
          status: 'failed',
          runId: get().assessmentRun.runId,
          bidId,
          errorMessage: 'Assessment timed out',
          itemCount: null,
          triggeredByUser: get().assessmentRun.triggeredByUser,
        },
      })
      return
    }

    let run: ExtractionRunSummary | null = null
    try {
      run = await getAssessmentRun(bidId)
    } catch (error) {
      // Transient — a dropped poll is not a failed run. Keep going.
      console.warn('Assessment run poll failed:', error)
      return
    }

    if (!ownsPoll()) return

    // No row yet, or still the pre-trigger one: keep waiting.
    if (!run || run.id === ignoreRunId) return
    found = true

    if (run.status === 'completed' || run.status === 'failed') {
      stopAssessmentPoll()
      set({
        assessmentRun: {
          status: run.status,
          runId: run.id,
          bidId,
          errorMessage: run.errorMessage,
          itemCount: run.itemCount,
          triggeredByUser: get().assessmentRun.triggeredByUser,
        },
      })
      if (run.status === 'completed') {
        void get().loadCategory(bidId, 'compliance_matrix')
      }
      return
    }

    // pending/running — keep the run id fresh so the UI can link to it.
    if (get().assessmentRun.runId !== run.id) {
      set({
        assessmentRun: {
          status: 'running',
          runId: run.id,
          bidId,
          errorMessage: null,
          itemCount: null,
          triggeredByUser: get().assessmentRun.triggeredByUser,
        },
      })
    }
  }

  // A stop (unmount) or a newer trigger landed during the caller's awaits.
  // Arming now would resurrect a poll nobody can reach.
  if (assessmentPollGeneration !== generation) return

  assessmentPollTimer = setInterval(() => {
    void tick()
  }, ASSESSMENT_POLL_INTERVAL_MS.value)
}

/**
 * Pick up the assessment the SERVER starts by itself once a compliance matrix
 * is written — after `extraction:complete` on the streaming path, in finalize
 * on the deferred-sweep path.
 *
 * Nothing used to watch for it: the poll was only armed by a click or by the
 * view's mount-time resume, and the view is already mounted while extraction
 * runs. So the matrix sat on "unassessed" with an Assess button until the user
 * clicked it — which ran the same assessment a second time.
 *
 * The run row is inserted by an async Lambda a moment AFTER the extraction
 * completes, so there is usually nothing to adopt yet. The poll is armed the
 * way `startAssessment` arms it — ignoring the newest finished run from before
 * this point, adopting one still in flight — plus a short adoption window: the
 * server may legitimately start nothing, and a run nobody asked for must not
 * spin for 15 minutes and then report a timeout.
 *
 * Blind spot: a run that has already FINISHED when the probe lands looks like
 * history and is ignored; the window then lapses quietly. Only a run with
 * nothing to score is that fast after a stream. After a sweep (whose watcher
 * sees completion up to one poll late) a very small run could be; the reload
 * the completion itself fires goes out alongside the probe, so in practice it
 * carries those positions and only the notice is lost.
 */
async function adoptServerStartedAssessment(
  bidId: string,
  get: StoreGet,
  set: StoreSet
): Promise<void> {
  if (get().currentBidId !== bidId) return
  // Already watching this bid — the user clicked Assess, or an earlier
  // completion adopted a run. A second poll would orphan the first.
  const current = get().assessmentRun
  if (current.status === 'running' && current.bidId === bidId) return

  const setPartial = (partial: Partial<ExtractionStoreState>): void => set(() => partial)

  // Same contract as `startAssessment`: stop and capture the generation before
  // the probe, and claim `running` synchronously. The status doubles as the
  // re-entrancy guard, so an Assess click in this window cannot fire the
  // assessment the server is already running.
  stopAssessmentPoll()
  const generation = assessmentPollGeneration
  setPartial({
    assessmentRun: {
      status: 'running',
      runId: null,
      bidId,
      errorMessage: null,
      itemCount: null,
      triggeredByUser: false,
    },
  })

  let prior: ExtractionRunSummary | null = null
  try {
    prior = await getAssessmentRun(bidId)
  } catch (error) {
    // Without the prior run's id, a stale finished run would read as ours and
    // announce work that never happened. Stand down instead: all that is lost
    // is the auto-refresh, and the Assess button comes back.
    console.warn('Could not check for the post-extraction assessment:', error)
    if (assessmentPollGeneration === generation) {
      setPartial({ assessmentRun: createEmptyAssessmentRun() })
    }
    return
  }

  // A stop or a real trigger landed while we were probing: not ours any more.
  if (assessmentPollGeneration !== generation) return
  if (get().currentBidId !== bidId) {
    setPartial({ assessmentRun: createEmptyAssessmentRun() })
    return
  }

  if (prior && (prior.status === 'pending' || prior.status === 'running')) {
    // Already in flight: the server de-dupes a trigger into a running run, so
    // this is the run that scores the new rows. Same rule as `startAssessment`.
    setPartial({
      assessmentRun: {
        status: 'running',
        runId: prior.id,
        bidId,
        errorMessage: null,
        itemCount: null,
        triggeredByUser: false,
      },
    })
    armAssessmentPoll({ get, set: setPartial, bidId, generation, ignoreRunId: null })
    return
  }

  armAssessmentPoll({
    get,
    set: setPartial,
    bidId,
    generation,
    ignoreRunId: prior?.id ?? null,
    adoptWithinMs: ASSESSMENT_ADOPTION_WINDOW_MS.value,
  })
}

/** Build a set-updater that rewrites the compliance matrix's items. */
function mapComplianceItems(
  mapper: (item: ExtractedItem) => ExtractedItem
): (state: ExtractionStoreState) => Partial<ExtractionStoreState> {
  return (state) => ({
    categories: {
      ...state.categories,
      compliance_matrix: {
        ...state.categories.compliance_matrix,
        items: state.categories.compliance_matrix.items.map(mapper),
      },
    },
  })
}

/**
 * Merge compliance-only fields into one item.
 *
 * The cast is safe by construction — `items` for `compliance_matrix` only ever
 * holds compliance rows; the union type comes from `CategoryState` being shared
 * across all five categories.
 */
function withComplianceFields(
  item: ExtractedItem,
  patch: Partial<ExtractedComplianceItem>
): ExtractedItem {
  return { ...(item as ExtractedComplianceItem), ...patch } as ExtractedItem
}

/**
 * What the server does to a row when a person sets its position (the PATCH
 * handler, which bulk also calls once per row): "Unassessed" hands the row
 * back to the AI and clears the whole verdict (`clearPosition`), so it reads
 * "Not assessed"; anything else is a manual position, which also drops the
 * AI's unassessed reason (mig 180). Shared by the single and bulk paths so an
 * optimistic row always matches what a reload would show — neither can bring
 * "Needs your confirmation" back. The client never sends provenance; it only
 * mirrors the server's stamp.
 */
function positionChangeFields(position: PositionValue): Partial<ExtractedComplianceItem> {
  return position === 'unassessed'
    ? {
        position,
        positionSource: 'ai',
        positionRationale: null,
        positionConfidence: null,
        positionCitations: null,
        positionAssessedAt: null,
        aiPosition: null,
        unassessedReason: null,
      }
    : { position, positionSource: 'manual', unassessedReason: null }
}

function findComplianceItem(
  state: ExtractionStoreState,
  itemId: string
): ExtractedItem | undefined {
  return state.categories.compliance_matrix.items.find((item) => hasId(item, itemId))
}

/**
 * Restore only `keys` from `prior` onto the item's CURRENT value — so an
 * unrelated edit made while the failed request was in flight survives the
 * rollback.
 *
 * The delete branch is what a `{ ...item, [key]: prior[key] }` patch cannot
 * express: under `exactOptionalPropertyTypes` an optional field's absence is
 * distinct from `undefined`, so a field that had no value before must have its
 * key removed, not set to undefined.
 */
function restoreComplianceFields(
  item: ExtractedItem,
  prior: ExtractedItem,
  keys: (keyof ExtractedComplianceItem)[]
): ExtractedItem {
  // The copy is widened to a string-keyed record for the write: TypeScript
  // collapses the write type of a union-keyed index to `never`, so a
  // `next[key] = value` on the typed shape cannot compile. `keys` is still
  // `keyof ExtractedComplianceItem`, so call sites remain checked.
  const next = { ...(item as ExtractedComplianceItem) } as Record<string, unknown>
  const from = prior as ExtractedComplianceItem
  for (const key of keys) {
    const value = from[key]
    if (value === undefined) delete next[key]
    else next[key] = value
  }
  return next as unknown as ExtractedItem
}

// Cap on how long fullReextract waits for the drip-feed reveal to finish
// before reloading the merged server result. Exported mutable so tests can
// shorten it (the interval is content-driven, not this).
export const RUN_SETTLE_TIMEOUT_MS = { value: 15_000 }

/**
 * Resolve once a category's run has settled: the drip-feed queue has drained
 * AND the category has left the `extracting` state (finalized to populated, or
 * dropped to empty on error). `startExtraction`'s promise resolves when the
 * stream completes, but the reveal keeps draining on a timer afterwards — a
 * reload issued before then would be either skipped by loadCategory's
 * extracting-guard or clobbered by the late finalize commit.
 */
async function waitForRunSettled(
  category: ExtractionCategory,
  get: StoreGet
): Promise<void> {
  const deadline = Date.now() + RUN_SETTLE_TIMEOUT_MS.value
  while (Date.now() < deadline) {
    const settled =
      get().categories[category].state !== 'extracting' &&
      dripQueues[category] === undefined
    if (settled) return
    await new Promise((resolve) => setTimeout(resolve, 15))
  }
}

// ===================
// Store Interface
// ===================

interface ExtractionStoreState {
  categories: Record<ExtractionCategory, CategoryState>
  currentBidId: string | null
  abortControllers: Record<ExtractionCategory, AbortController | null>

  // Compliance loop
  assessmentRun: AssessmentRunState
  /** Null when never fetched, or when the member lacks write access (see loadStaleness). */
  staleness: ComplianceStaleness | null

  // Data loading
  loadCategory: (bidId: string, category: ExtractionCategory) => Promise<void>
  loadAllCategories: (bidId: string) => Promise<void>

  // Extraction
  startExtraction: (bidId: string, category: ExtractionCategory) => Promise<void>
  cancelExtraction: (category: ExtractionCategory) => void

  // Re-extraction
  findNewItems: (bidId: string, category: ExtractionCategory) => Promise<void>
  fullReextract: (bidId: string, category: ExtractionCategory) => Promise<void>
  acceptNewItem: (bidId: string, category: ExtractionCategory, index: number) => Promise<void>
  dismissNewItem: (bidId: string, category: ExtractionCategory, index: number) => Promise<void>
  acceptAllNewItems: (bidId: string, category: ExtractionCategory) => Promise<void>
  dismissAllNewItems: (bidId: string, category: ExtractionCategory) => Promise<void>

  // Removed-items review pile (superseded rows from a merge re-run)
  restoreRemovedItem: (bidId: string, category: ExtractionCategory, itemId: string) => Promise<void>
  confirmRemoveItem: (bidId: string, category: ExtractionCategory, itemId: string) => Promise<void>

  // Item CRUD
  updateItemStatus: (bidId: string, category: ExtractionCategory, itemId: string, status: string) => Promise<void>
  updateItemNote: (bidId: string, category: ExtractionCategory, itemId: string, note: string | null) => Promise<void>
  updateItemOwner: (bidId: string, category: ExtractionCategory, itemId: string, ownerId: string | null) => Promise<void>
  updateItemSeverity: (bidId: string, itemId: string, severity: 'critical' | 'high' | 'medium' | 'low') => Promise<void>
  linkSubmissionItem: (bidId: string, itemId: string, linkedArtifactId: string, linkedSectionKey: string | null) => Promise<void>
  unlinkSubmissionItem: (bidId: string, itemId: string) => Promise<void>
  /**
   * Resolves with the row the server created. Callers need it to work out
   * whether the row they just added is actually visible under the table's
   * active filters — a manual row defaults to values that a narrowed table
   * often excludes, and a save that lands off-screen reads as a failure.
   */
  addManualItem: (bidId: string, category: ExtractionCategory, item: Partial<ExtractedItem>) => Promise<ExtractedItem>
  deleteItem: (bidId: string, category: ExtractionCategory, itemId: string) => Promise<void>

  // Compliance positions + AI assessment
  updateItemPosition: (bidId: string, itemId: string, position: PositionValue) => Promise<void>
  updateItemCriticality: (bidId: string, itemId: string, criticality: CriticalityValue) => Promise<void>
  confirmPositions: (bidId: string, itemIds: string[]) => Promise<void>
  startAssessment: (bidId: string, itemIds?: string[]) => Promise<void>
  /** Re-attach to a run left in flight by a previous mount. Call on mount. */
  resumeAssessmentPolling: (bidId: string) => Promise<void>
  stopAssessmentPolling: () => void
  loadStaleness: (bidId: string) => Promise<void>

  // Bulk operations
  /**
   * `statusSource` is named rather than left to the index signature: handing a
   * row back to derivation is a `{ statusSource: 'derived' }`-only PATCH, and a
   * typo in that one string would silently do nothing at all.
   */
  bulkPatchItems: (
    bidId: string,
    category: ExtractionCategory,
    itemIds: string[],
    patch: Record<string, unknown> & { statusSource?: 'derived' | 'manual' },
  ) => Promise<void>
  bulkDeleteItems: (bidId: string, category: ExtractionCategory, itemIds: string[]) => Promise<void>

  // Unseen-completion chip
  markCategoryViewed: (category: ExtractionCategory) => void

  // Getters
  getCategoryState: (category: ExtractionCategory) => CategoryState
  getUnseenCompletionCount: () => number

  // Reset
  reset: () => void
}

// ===================
// Store
// ===================

export const useExtractionStore = create<ExtractionStoreState>()(
  devtools(
    (set, get) => ({
      categories: createEmptyCategories(),
      currentBidId: null,
      abortControllers: {
        evaluation_criteria: null,
        compliance_matrix: null,
        submission_checklist: null,
        key_dates: null,
        risk_register: null,
      },
      assessmentRun: createEmptyAssessmentRun(),
      staleness: null,

      loadCategory: async (bidId: string, category: ExtractionCategory) => {
        // Don't overwrite in-progress extraction — streaming callbacks manage that state.
        //
        // Scoped to the bid being extracted. An extraction running on bid A used
        // to block this load for EVERY bid: switch bids mid-run and the category
        // never loaded the new bid's data, leaving bid A's partial items on bid
        // B's page indefinitely. A run on another bid says nothing about whether
        // this bid's data can be fetched.
        //
        // Only a *known* different bid lifts the block. A null `currentBidId`
        // means the store is not scoped yet, which is no evidence the run belongs
        // elsewhere — deferring to the stream stays the safe default there.
        const { categories, currentBidId } = get()
        const runBelongsToAnotherBid = currentBidId !== null && currentBidId !== bidId
        if (categories[category].state === 'extracting' && !runBelongsToAnotherBid) return

        try {
          const data = await api.get<ExtractionCategoryResponse>(
            `/bids/${bidId}/extraction/${category}?includeRemoved=true`
          )
          const items = Array.isArray(data?.items) ? data.items : []
          const removedItems = Array.isArray(data?.removedItems) ? data.removedItems : []
          const dismissedTexts = Array.isArray(data?.dismissedTexts) ? data.dismissedTexts : []
          const lastExtractedAt = data?.lastExtractedAt ?? null
          set((state) => {
            const current = state.categories[category]
            // The Find-new-items review list. Since mig 194 the server returns
            // it (`awaitingItems`), which is what brings it back after a
            // reload; an older backend returns those rows among `items`, so
            // they are held out of the table. Either way a row is on screen
            // once — in the review list until decided.
            const { newItems, items: tableItems } = reviewListForLoad(
              { ...data, items },
              current.newItems
            )
            // A category whose only rows await review is populated, not empty
            // — the review banner lives in the populated view. (An older
            // backend's awaiting rows are already counted in `items`.)
            const hasRows =
              items.length > 0 || (Array.isArray(data?.awaitingItems) && newItems.length > 0)

            // Re-check after the await: if a stream started while this REST
            // call was in flight, defer to the streaming pipeline. Without
            // this check, a slow REST response can demote state from
            // 'extracting' back to 'empty' (with isRecovering=false), leaving
            // the user staring at the bare "Extract X" button while the
            // Lambda is still running.
            if (current.state === 'extracting') {
              if (items.length === 0) return state
              // Items already persisted — bypass the stream and populate now.
              return {
                categories: {
                  ...state.categories,
                  [category]: {
                    ...current,
                    state: 'populated' as ExtractionState,
                    items: tableItems,
                    newItems,
                    isReviewingNewItems: newItems.length > 0,
                    removedItems,
                    dismissedTexts,
                    itemCount: tableItems.length,
                    lastExtractedAt,
                    streamError: null,
                    isRecovering: false,
                  },
                },
              }
            }

            // Same defence for the recovery path — never demote to bare empty
            // while polling is in flight.
            if (current.isRecovering && items.length === 0) return state

            return {
              categories: {
                ...state.categories,
                [category]: {
                  ...current,
                  state: hasRows ? ('populated' as ExtractionState) : ('empty' as ExtractionState),
                  items: tableItems,
                  newItems,
                  isReviewingNewItems: newItems.length > 0,
                  removedItems,
                  dismissedTexts,
                  itemCount: tableItems.length,
                  lastExtractedAt,
                  // Successful load clears any prior stream failure.
                  ...(hasRows ? { streamError: null, isRecovering: false } : {}),
                },
              },
              currentBidId: bidId,
            }
          })
        } catch (error) {
          console.error(`Failed to load ${category}:`, error)
          // Don't throw — allow other categories to load
        }
      },

      loadAllCategories: async (bidId: string) => {
        const persistedUnseen = loadUnseenFromStorage(bidId)
        set((state) => {
          // `currentBidId` flips synchronously here, but the five per-category
          // REST calls below take a moment to land. Carrying the PREVIOUS bid's
          // categories through that window makes every consumer read bid A's
          // `lastExtractedAt` and assert that bid B ran and found nothing —
          // a positive false claim about a tender that was never analysed.
          // Wipe on a bid switch only; a same-bid reload keeps its state so
          // the in-flight guards in `loadCategory` still have something to see.
          const nextCategories = hasMovedToAnotherBid(state.currentBidId, bidId)
            ? createEmptyCategories()
            : { ...state.categories }
          for (const cat of CATEGORIES) {
            nextCategories[cat] = {
              ...nextCategories[cat],
              hasUnseenCompletion: persistedUnseen[cat] === true,
            }
          }
          return { categories: nextCategories, currentBidId: bidId }
        })
        await Promise.allSettled(
          CATEGORIES.map((cat) => get().loadCategory(bidId, cat))
        )

        // After items are loaded, check for any extraction runs still in flight
        // (e.g. user refreshed the page mid-extraction). For each running run
        // whose items haven't landed yet, seed the recovery loop so the stepper
        // re-appears and the phase keeps advancing.
        try {
          const runs = await api.get<ExtractionRunSummary[]>(`/bids/${bidId}/extraction-runs`)
          if (!Array.isArray(runs)) return
          // Bail if the user navigated to a different bid while runs were loading.
          if (get().currentBidId !== bidId) return

          for (const cat of CATEGORIES) {
            const latest = latestRunForCategory(runs, cat)
            // A finished partial read keeps saying so after a reload (or when
            // the user comes back from the "analysis ready" notice), until the
            // next run starts.
            if (latest?.status === 'completed' && latest.partialRead === true) {
              set((state) => ({
                categories: {
                  ...state.categories,
                  [cat]: { ...state.categories[cat], partialRead: true },
                },
              }))
              continue
            }
            if (!latest || latest.status !== 'running') continue

            // Async sweep: attach the watcher REGARDLESS of whether items are
            // already loaded. A re-run sits on top of the previous run's matrix
            // for 20–30 minutes, so the empty-guard below would make an
            // in-flight sweep invisible after any refresh — no stepper, no
            // watcher, no completion.
            if (isSweepRun(latest, cat)) {
              const sweepPhase = latest.phase ?? null
              const sweepProgress: SweepProgress | null = latest.sweepProgress ?? null
              set((state) => ({
                categories: {
                  ...state.categories,
                  [cat]: {
                    ...state.categories[cat],
                    state: 'extracting' as ExtractionState,
                    phase: sweepPhase,
                    sweepProgress,
                    isRecovering: false,
                    streamError: null,
                  },
                },
              }))
              watchSweepRun(bidId, cat, latest.id, get, set)
              continue
            }

            const current = get().categories[cat]
            // Skip if items already landed (loadCategory already populated us)
            // or another flow is already managing the streaming/recovery state.
            if (current.state !== 'empty') continue
            if (current.isRecovering) continue
            if (get().abortControllers[cat] !== null) continue

            // Seed phase from the latest persisted value (may be null very
            // early in a run) and start polling. The recovery loop will keep
            // phase fresh and finalize when items land or status flips.
            set((state) => ({
              categories: {
                ...state.categories,
                [cat]: {
                  ...state.categories[cat],
                  phase: latest.phase ?? null,
                },
              },
            }))
            startBackgroundRecovery(
              bidId,
              cat,
              latest.errorMessage ?? 'Extraction interrupted',
              get,
              set
            )
          }
        } catch {
          // Non-critical — the user can still kick off a fresh extraction.
        }
      },

      startExtraction: async (bidId: string, category: ExtractionCategory) => {
        if (!isExtractionStreamingAvailable()) {
          console.error(
            `Extraction failed for ${category}:`,
            new Error('VITE_EXTRACTION_STREAM_URL is not configured')
          )
          return
        }

        // Cancel any existing extraction for this category
        get().cancelExtraction(category)

        const controller = new AbortController()

        set((state) => ({
          categories: {
            ...state.categories,
            [category]: {
              ...state.categories[category],
              state: 'extracting' as ExtractionState,
              streamingItems: [],
              // Fresh run supersedes any prior recovery attempt.
              isRecovering: false,
              streamError: null,
              phase: null,
              sweepProgress: null,
              isSweeping: false,
              // A new run answers for itself; the last run's partial read no
              // longer describes the table it will produce.
              partialRead: false,
            },
          },
          abortControllers: {
            ...state.abortControllers,
            [category]: controller,
          },
          // Pin currentBidId so drip-feed and recovery polling can detect
          // navigation-away. Normally set by loadAllCategories on mount, but
          // some flows (manual retry, tests) start extraction without it.
          currentBidId: state.currentBidId ?? bidId,
        }))

        // Set when the backend deferred this run to the async sweep worker.
        // The stream then ends BY DESIGN with no items and no completion, and
        // the watcher owns the run from that point on.
        let queued = false
        // Items that matched a row still awaiting review (mig 194). A full run
        // never promotes those, so they are not table rows — the reload after
        // the run puts them back in the review list.
        let heldForReview = 0

        try {
          await streamExtraction(
            { bidId, category },
            {
              onQueued: (runId) => {
                queued = true
                // The category stays `extracting` (set above), so the stepper
                // keeps showing while the worker sweeps.
                watchSweepRun(bidId, category, runId, get, set)
              },
              onProgress: (phase) => {
                set((state) => ({
                  categories: {
                    ...state.categories,
                    [category]: {
                      ...state.categories[category],
                      phase,
                    },
                  },
                }))
              },
              onItem: (item, _index, meta) => {
                if (meta?.outcome === 'awaiting') {
                  heldForReview += 1
                  return
                }
                // Reveal-only drip: queue the item, start the timer if needed.
                // Reduced-motion users skip drip entirely (commit immediately).
                if (prefersReducedMotion()) {
                  set((state) => ({
                    categories: {
                      ...state.categories,
                      [category]: {
                        ...state.categories[category],
                        streamingItems: [...state.categories[category].streamingItems, item],
                      },
                    },
                  }))
                  return
                }
                enqueueDripItem(bidId, category, item, set, get)
              },
              onComplete: (streamedTotal, info) => {
                if (info?.partial) {
                  // The server could not read the whole answer and kept the
                  // rows it did not reach. Set before the drip finalises, so
                  // the notice is there when the table appears.
                  set((state) => ({
                    categories: {
                      ...state.categories,
                      [category]: { ...state.categories[category], partialRead: true },
                    },
                  }))
                }
                // The server assesses the new rows by itself right after this
                // event. Not awaited — the adoption owns its own poll.
                if (category === 'compliance_matrix') {
                  void adoptServerStartedAssessment(bidId, get, set)
                }
                // The server's total counts every streamed item, including
                // the ones held for review above.
                const total = Math.max(0, streamedTotal - heldForReview)
                if (prefersReducedMotion()) {
                  finalizeRun(category, total, set, get)
                  return
                }
                // Mark the queue complete; the drip drainer will finalize when
                // the last item ticks out.
                const queue = dripQueues[category]
                if (queue && queue.bidId === bidId) {
                  queue.completeTotal = total
                  // If items already drained before complete arrived (rare —
                  // network sometimes delivers complete after all items are
                  // already off the queue), finalize immediately.
                  if (queue.items.length === 0) {
                    clearDripQueue(category)
                    finalizeRun(category, total, set, get)
                  }
                } else {
                  // Lambda completed with zero items — no drip needed.
                  finalizeRun(category, total, set, get)
                }
              },
              onError: (message) => {
                console.error(`Extraction error for ${category}:`, message)
                // Drop any in-flight drip-feed before falling back.
                clearDripQueue(category)
                // Clear extracting state so loadCategory fallback isn't blocked.
                // Set isRecovering atomically so the UI moves directly into the
                // "still working" recovery view — never flashes the "no items yet"
                // empty state between here and startBackgroundRecovery below.
                set((state) => ({
                  categories: {
                    ...state.categories,
                    [category]: {
                      ...state.categories[category],
                      state: 'empty' as ExtractionState,
                      streamingItems: [],
                      isRecovering: true,
                      streamError: null,
                    },
                  },
                }))
                // Fall back to loading any persisted items, then keep polling
                // in the background — Lambda may finish persisting after the
                // stream errored.
                void (async () => {
                  // A deferred sweep may already be running server-side. Hand it
                  // to the watcher: items-based recovery would finalise on the
                  // PREVIOUS run's rows within one tick.
                  if (await tryAttachRunningSweep(bidId, category, get, set)) return
                  await get().loadCategory(bidId, category)
                  if (get().categories[category].state !== 'populated') {
                    startBackgroundRecovery(bidId, category, message, get, set)
                  }
                })()
              },
            },
            controller.signal
          )
          // A queued sweep has no result yet — waiting for it to settle would
          // burn the settle budget and the reload would clobber the stepper
          // with the PREVIOUS run's rows. The watcher finalises instead.
          if (queued) return

          // Defensive: the stream ended cleanly, nothing landed, and the
          // category is still `extracting` — an `extraction:queued` event that
          // carried no `runId`, or a truncated terminal event. Ask the run
          // record whether a sweep is actually live before falling through to
          // the settle-and-reload tail, which would spin its budget and leave
          // the stepper up with nobody watching.
          if (
            get().currentBidId === bidId &&
            get().categories[category].state === 'extracting' &&
            dripQueues[category] === undefined &&
            (await tryAttachRunningSweep(bidId, category, get, set))
          ) {
            return
          }

          // After a successful stream the backend holds the persisted/merged
          // rows (real ids, superseded rows) — the streamed items are the raw
          // extraction and lack real ids. Wait for the drip reveal to settle,
          // then reload once so the UI reflects the server truth. Mirrors the
          // old fullReextract tail; fullReextract now just calls startExtraction.
          await waitForRunSettled(category, get)
          if (
            get().currentBidId === bidId &&
            get().categories[category].state === 'populated'
          ) {
            await get().loadCategory(bidId, category)
          }
          syncBidDeadlineAfterRun(bidId, category)
        } catch (error) {
          if ((error as Error).name !== 'AbortError') {
            console.error(`Extraction failed for ${category}:`, error)
            // Drop any in-flight drip-feed before falling back.
            clearDripQueue(category)
            // The stream can drop in the narrow window between the server
            // dispatching a deferred sweep and writing `extraction:queued`. If
            // that run is live, the watcher owns it — the items-based recovery
            // below would read the PREVIOUS run's rows as this run's success.
            if (await tryAttachRunningSweep(bidId, category, get, set)) return
            // Clear extracting state so loadCategory fallback isn't blocked.
            // Set isRecovering atomically so the UI moves directly into the
            // "still working" recovery view — never flashes the "no items yet"
            // empty state between here and startBackgroundRecovery below.
            set((state) => ({
              categories: {
                ...state.categories,
                [category]: {
                  ...state.categories[category],
                  state: 'empty' as ExtractionState,
                  streamingItems: [],
                  isRecovering: true,
                  streamError: null,
                },
              },
            }))
            // Fall back to loading persisted items from REST API.
            // The Lambda may have completed and saved items even if the stream was interrupted.
            await get().loadCategory(bidId, category)
            // If the immediate fallback didn't recover items (Lambda still
            // running), kick off background polling.
            if (get().categories[category].state !== 'populated') {
              startBackgroundRecovery(
                bidId,
                category,
                (error as Error).message || 'Connection lost',
                get,
                set
              )
            }
          }
        } finally {
          set((state) => ({
            abortControllers: {
              ...state.abortControllers,
              [category]: null,
            },
          }))
        }
      },

      cancelExtraction: (category: ExtractionCategory) => {
        const controller = get().abortControllers[category]
        if (controller) {
          controller.abort()
          set((state) => ({
            abortControllers: {
              ...state.abortControllers,
              [category]: null,
            },
          }))
        }
        // Drop any pending drip-feed for this category.
        clearDripQueue(category)
        // Stop watching any async sweep for this category. The server run is
        // NOT cancelled (there is no server-side cancel) — this only ends the
        // poll, and a fresh run re-attaches with its own runId.
        delete sweepWatchers[category]
        // Nothing is watching this category any more, so it must stop claiming
        // a live sweep — otherwise the stepper keeps promising a completion
        // notice for a poll loop that no longer exists.
        set((state) => ({
          categories: {
            ...state.categories,
            [category]: { ...state.categories[category], isSweeping: false },
          },
        }))
      },

      findNewItems: async (bidId: string, category: ExtractionCategory) => {
        if (!isExtractionStreamingAvailable()) {
          console.error(
            `Find new items failed for ${category}:`,
            new Error('VITE_EXTRACTION_STREAM_URL is not configured')
          )
          return
        }

        const controller = new AbortController()

        set((state) => ({
          abortControllers: {
            ...state.abortControllers,
            [category]: controller,
          },
        }))

        // The run SAVES everything it finds before streaming it, and says which
        // row each item became. "New" is therefore the server's call, not a
        // title comparison here. With `find_new` the server holds a new row
        // `awaiting_review` (outcome `awaiting`, mig 194) — out of the table
        // until the user decides. An older backend, or the deploy gap before
        // mig 194, saves it active instead (outcome `inserted`); the store then
        // holds it out of the table itself. A match (an existing row, or a
        // dismissed tombstone) is already known. Without a row id there is
        // nothing safe to act on, so the item is not offered — the reload
        // below still shows what was saved.
        const found: FoundRow[] = []
        let aborted = false

        try {
          await streamExtraction(
            { bidId, category, mode: 'find_new' },
            {
              onItem: (item, _index, meta) => {
                const outcome = meta?.outcome
                if ((outcome === 'awaiting' || outcome === 'inserted') && meta.id) {
                  found.push({ id: meta.id, outcome, streamed: item })
                }
              },
              onComplete: () => {
                // The server persisted this run too, so it may have set the deadline.
                syncBidDeadlineAfterRun(bidId, category)
              },
              onError: (message) => {
                console.error(`Find new items error for ${category}:`, message)
              },
            },
            controller.signal
          )
        } catch (error) {
          if ((error as Error).name === 'AbortError') {
            aborted = true
          } else {
            console.error(`Find new items failed for ${category}:`, error)
          }
        }

        try {
          // Re-read the table even after a dropped stream: the run saves as it
          // goes, so rows may exist that the client never heard about. Done
          // before the controller clears, so "finding" stays on until the
          // table is settled.
          if (!aborted) await reconcileFoundRows(set, bidId, category, found)
        } finally {
          set((state) => ({
            abortControllers: {
              ...state.abortControllers,
              [category]: null,
            },
          }))
        }
      },

      fullReextract: async (bidId: string, category: ExtractionCategory) => {
        // Re-run honesty (PR 2): the backend now MERGES on re-run — surviving
        // ids, refreshed statuses, superseded rows. Clear any in-flight
        // new-items review, then re-extract. The reload of the merged server
        // result is owned by startExtraction's success tail (which every
        // extraction now runs), so there's nothing extra to do here. Rows the
        // run matched that are still awaiting review (mig 194: a full run never
        // promotes them) come back into the review list with that reload.
        set((state) => ({
          categories: {
            ...state.categories,
            [category]: {
              ...state.categories[category],
              newItems: [],
              isReviewingNewItems: false,
            },
          },
        }))
        await get().startExtraction(bidId, category)
      },

      // Find new items already SAVED every row it offers for review, so each
      // decision acts on that row by id (PATCH `review`). Never POST `/manual`
      // here: that inserted a second copy (accept) or a separate tombstone
      // beside the still-active row (dismiss) — the duplicate-row bug.
      acceptNewItem: async (bidId: string, category: ExtractionCategory, index: number) => {
        const item = get().categories[category].newItems[index]
        if (!item) return
        const result = await reviewSavedRow(bidId, category, item, 'accept')
        settleReviews(set, get, bidId, category, 'accept', [{ item, result }])
      },

      dismissNewItem: async (bidId: string, category: ExtractionCategory, index: number) => {
        const item = get().categories[category].newItems[index]
        if (!item) return
        const result = await reviewSavedRow(bidId, category, item, 'dismiss')
        settleReviews(set, get, bidId, category, 'dismiss', [{ item, result }])
      },

      acceptAllNewItems: async (bidId: string, category: ExtractionCategory) => {
        const items = get().categories[category].newItems
        if (items.length === 0) return
        const results = await Promise.all(
          items.map(async (item) => ({
            item,
            result: await reviewSavedRow(bidId, category, item, 'accept'),
          }))
        )
        settleReviews(set, get, bidId, category, 'accept', results)
      },

      dismissAllNewItems: async (bidId: string, category: ExtractionCategory) => {
        const items = get().categories[category].newItems
        if (items.length === 0) return
        const results = await Promise.all(
          items.map(async (item) => ({
            item,
            result: await reviewSavedRow(bidId, category, item, 'dismiss'),
          }))
        )
        settleReviews(set, get, bidId, category, 'dismiss', results)
      },

      restoreRemovedItem: async (bidId: string, category: ExtractionCategory, itemId: string) => {
        const item = get().categories[category].removedItems.find((it) => hasId(it, itemId))
        if (!item) return
        try {
          const { item: serverItem, lifecycleState } = await api.patch<{
            item: ExtractedItem
            lifecycleState?: string
          }>(`/bids/${bidId}/extraction/${category}/${itemId}`, { lifecycleState: 'active' })
          set((state) => {
            const cat = state.categories[category]
            // A row that was never accepted goes back to awaiting review, not
            // into the table (mig 194) — the server says where it landed.
            if (lifecycleState === 'awaiting_review') {
              const newItems = [
                ...cat.newItems.filter((it) => !hasId(it, itemId)),
                // The PATCH maps the one row, so a criterion keeps its children.
                acceptedRow(serverItem, item),
              ]
              return {
                categories: {
                  ...state.categories,
                  [category]: {
                    ...cat,
                    newItems,
                    isReviewingNewItems: true,
                    removedItems: cat.removedItems.filter((it) => !hasId(it, itemId)),
                  },
                },
              }
            }
            return {
              categories: {
                ...state.categories,
                [category]: {
                  ...cat,
                  items: [...cat.items, serverItem],
                  itemCount: cat.itemCount + 1,
                  removedItems: cat.removedItems.filter((it) => !hasId(it, itemId)),
                },
              },
            }
          })
        } catch (error) {
          console.error(`Failed to restore ${category} item:`, error)
        }
      },

      confirmRemoveItem: async (bidId: string, category: ExtractionCategory, itemId: string) => {
        const item = get().categories[category].removedItems.find((it) => hasId(it, itemId))
        if (!item) return
        const text = itemMatchText(item)
        try {
          await api.patch(`/bids/${bidId}/extraction/${category}/${itemId}`, {
            lifecycleState: 'dismissed',
          })
          set((state) => {
            const cat = state.categories[category]
            return {
              categories: {
                ...state.categories,
                [category]: {
                  ...cat,
                  removedItems: cat.removedItems.filter((it) => !hasId(it, itemId)),
                  dismissedTexts: text ? [...cat.dismissedTexts, text] : cat.dismissedTexts,
                },
              },
            }
          })
        } catch (error) {
          console.error(`Failed to remove ${category} item:`, error)
        }
      },

      updateItemStatus: async (bidId: string, category: ExtractionCategory, itemId: string, status: string) => {
        // Optimistic update. `statusSource: 'manual'` mirrors what the server
        // stamps for any PATCH carrying a status — without it a compliance row
        // whose Progress was being derived from response coverage would snap
        // straight back to the derived pill, discarding the choice on screen.
        // Harmless on the categories that have no such column.
        set((state) => ({
          categories: {
            ...state.categories,
            [category]: {
              ...state.categories[category],
              items: state.categories[category].items.map((item) =>
                hasId(item, itemId) ? { ...item, status, statusSource: 'manual' } : item
              ),
            },
          },
        }))
        try {
          await api.patch(`/bids/${bidId}/extraction/${category}/${itemId}`, { status })
        } catch (error) {
          console.error('Failed to update item status:', error)
          // Reload to revert optimistic update
          await get().loadCategory(bidId, category)
        }
      },

      updateItemNote: async (bidId: string, category: ExtractionCategory, itemId: string, note: string | null) => {
        // Optimistic update
        set((state) => ({
          categories: {
            ...state.categories,
            [category]: {
              ...state.categories[category],
              items: state.categories[category].items.map((item) =>
                hasId(item, itemId) ? { ...item, note } : item
              ),
            },
          },
        }))
        try {
          await api.patch(`/bids/${bidId}/extraction/${category}/${itemId}`, { note })
        } catch (error) {
          console.error('Failed to update item note:', error)
          await get().loadCategory(bidId, category)
        }
      },

      updateItemOwner: async (bidId: string, category: ExtractionCategory, itemId: string, ownerId: string | null) => {
        // Optimistic update
        set((state) => ({
          categories: {
            ...state.categories,
            [category]: {
              ...state.categories[category],
              items: state.categories[category].items.map((item) =>
                hasId(item, itemId) ? { ...item, ownerId } : item
              ),
            },
          },
        }))
        try {
          await api.patch(`/bids/${bidId}/extraction/${category}/${itemId}`, { ownerId })
        } catch (error) {
          console.error('Failed to update item owner:', error)
          await get().loadCategory(bidId, category)
        }
      },

      updateItemSeverity: async (bidId: string, itemId: string, severity: 'critical' | 'high' | 'medium' | 'low') => {
        // Optimistic update
        set((state) => ({
          categories: {
            ...state.categories,
            risk_register: {
              ...state.categories.risk_register,
              items: state.categories.risk_register.items.map((item) =>
                hasId(item, itemId) ? { ...item, severity } : item
              ),
            },
          },
        }))
        try {
          await api.patch(`/bids/${bidId}/extraction/risk_register/${itemId}`, { severity })
        } catch (error) {
          console.error('Failed to update item severity:', error)
          await get().loadCategory(bidId, 'risk_register')
        }
      },

      linkSubmissionItem: async (bidId: string, itemId: string, linkedArtifactId: string, linkedSectionKey: string | null) => {
        try {
          await api.patch(`/bids/${bidId}/extraction/submission_checklist/${itemId}`, {
            linkedArtifactId,
            linkedSectionKey,
          })
          await get().loadCategory(bidId, 'submission_checklist')
        } catch (error) {
          console.error('Failed to link submission item:', error)
          throw error
        }
      },

      unlinkSubmissionItem: async (bidId: string, itemId: string) => {
        try {
          await api.patch(`/bids/${bidId}/extraction/submission_checklist/${itemId}`, {
            linkedArtifactId: null,
            linkedSectionKey: null,
          })
          await get().loadCategory(bidId, 'submission_checklist')
        } catch (error) {
          console.error('Failed to unlink submission item:', error)
          throw error
        }
      },

      addManualItem: async (bidId: string, category: ExtractionCategory, item: Partial<ExtractedItem>) => {
        try {
          const created = await api.post<{ item: ExtractedItem }>(
            `/bids/${bidId}/extraction/${category}/manual`,
            item
          )
          set((state) => ({
            categories: {
              ...state.categories,
              [category]: {
                ...state.categories[category],
                state: 'populated' as ExtractionState,
                items: [...state.categories[category].items, created.item],
                itemCount: state.categories[category].itemCount + 1,
              },
            },
          }))
          return created.item
        } catch (error) {
          console.error('Failed to add manual item:', error)
          throw error
        }
      },

      deleteItem: async (bidId: string, category: ExtractionCategory, itemId: string) => {
        // Optimistic update
        set((state) => ({
          categories: {
            ...state.categories,
            [category]: {
              ...state.categories[category],
              items: state.categories[category].items.filter(
                (item) => !hasId(item, itemId)
              ),
              itemCount: state.categories[category].itemCount - 1,
            },
          },
        }))
        try {
          await api.delete(`/bids/${bidId}/extraction/${category}/${itemId}`)
        } catch (error) {
          console.error('Failed to delete item:', error)
          await get().loadCategory(bidId, category)
        }
      },

      // ===================
      // Compliance positions + AI assessment
      // ===================

      updateItemPosition: async (bidId: string, itemId: string, position: PositionValue) => {
        const prior = findComplianceItem(get(), itemId)
        // Picking "Unassessed" WITHDRAWS a position rather than overriding one:
        // the server clears the whole verdict and hands the row back to the
        // assessor (`positionSource` → 'ai'). Mirror all of that optimistically,
        // or the row would show a stale rationale under a cleared pill, and a
        // "set manually — AI no longer updates this" badge that is now false.
        // Clearing also nulls ai_position (mig 156) — leaving it would make
        // the row read as an override of a verdict the server has just erased
        // — and the reason behind that verdict (mig 180). See
        // `positionChangeFields` for the whole rule.
        const fields = positionChangeFields(position)
        const touched = Object.keys(fields) as Array<keyof ExtractedComplianceItem>
        set(
          mapComplianceItems((item) =>
            hasId(item, itemId) ? withComplianceFields(item, fields) : item
          )
        )
        try {
          await api.patch(`/bids/${bidId}/extraction/compliance_matrix/${itemId}`, { position })
        } catch (error) {
          console.error('Failed to update item position:', error)
          if (prior) {
            set(
              mapComplianceItems((item) =>
                hasId(item, itemId) ? restoreComplianceFields(item, prior, touched) : item
              )
            )
          }
        }
      },

      updateItemCriticality: async (
        bidId: string,
        itemId: string,
        criticality: CriticalityValue
      ) => {
        const prior = findComplianceItem(get(), itemId)
        set(
          mapComplianceItems((item) =>
            hasId(item, itemId)
              ? withComplianceFields(item, { criticality, criticalitySource: 'manual' })
              : item
          )
        )
        try {
          await api.patch(`/bids/${bidId}/extraction/compliance_matrix/${itemId}`, { criticality })
        } catch (error) {
          console.error('Failed to update item criticality:', error)
          if (prior) {
            set(
              mapComplianceItems((item) =>
                hasId(item, itemId)
                  ? restoreComplianceFields(item, prior, ['criticality', 'criticalitySource'])
                  : item
              )
            )
          }
        }
      },

      confirmPositions: async (bidId: string, itemIds: string[]) => {
        if (itemIds.length === 0) return
        // Snapshot the whole affected set — confirm is one request, so a
        // failure means none of them moved.
        const snapshot = new Map<string, ExtractedItem>()
        for (const item of get().categories.compliance_matrix.items) {
          const id = (item as { id?: string | null }).id
          if (id && itemIds.includes(id)) snapshot.set(id, item)
        }

        const isTarget = (item: ExtractedItem): string | null => {
          const id = (item as { id?: string | null }).id
          return id && itemIds.includes(id) ? id : null
        }

        set(
          mapComplianceItems((item) =>
            isTarget(item) ? withComplianceFields(item, { positionSource: 'manual' }) : item
          )
        )
        try {
          await confirmCompliancePositions(bidId, itemIds)
        } catch (error) {
          console.error('Failed to confirm positions:', error)
          set(
            mapComplianceItems((item) => {
              const id = isTarget(item)
              const prior = id ? snapshot.get(id) : undefined
              return prior ? restoreComplianceFields(item, prior, ['positionSource']) : item
            })
          )
        }
      },

      startAssessment: async (bidId: string, itemIds?: string[]) => {
        // Re-entrancy guard. Set synchronously below before the first await, so
        // a double-clicked Assess button short-circuits here rather than firing
        // a second trigger and racing to arm a second interval.
        if (get().assessmentRun.status === 'running') return

        stopAssessmentPoll()
        const generation = assessmentPollGeneration

        set({
          assessmentRun: {
            status: 'running',
            runId: null,
            bidId,
            errorMessage: null,
            itemCount: null,
            triggeredByUser: true,
          },
        })

        // The newest run BEFORE we trigger. A terminal one must be ignored or
        // the first poll would report the previous run's outcome as this
        // one's — the worker is invoked asynchronously and inserts its row a
        // moment after the 202. A still-running one is adopted instead: the
        // backend de-dupes concurrent triggers, so that run IS ours.
        let prior: ExtractionRunSummary | null = null
        try {
          prior = await getAssessmentRun(bidId)
        } catch (error) {
          // Fail closed. Without a known prior run id we cannot tell a stale
          // terminal run from ours, and guessing would report an old run's
          // success as this one's — a false guarantee that requirements were
          // scored. Refusing costs the user one retry; guessing costs trust.
          console.error('Could not check for an existing assessment run:', error)
          set({
            assessmentRun: {
              status: 'failed',
              runId: null,
              bidId,
              errorMessage: 'Could not check for an existing assessment. Try again.',
              itemCount: null,
              triggeredByUser: true,
            },
          })
          return
        }
        const ignoreRunId =
          prior && (prior.status === 'completed' || prior.status === 'failed') ? prior.id : null

        try {
          const outcome = await startComplianceAssessment(bidId, itemIds)
          // Nothing was queued (server-side kill switch). No run row will ever
          // appear, so polling would burn 15 minutes and then report a timeout
          // that never happened. Say what actually occurred instead.
          if (!outcome.started) {
            set({
              assessmentRun: {
                status: 'failed',
                runId: null,
                bidId,
                errorMessage: 'AI assessment is turned off right now. Nothing was run.',
                itemCount: null,
                triggeredByUser: true,
              },
            })
            return
          }
        } catch (error) {
          console.error('Failed to start compliance assessment:', error)
          set({
            assessmentRun: {
              status: 'failed',
              runId: null,
              bidId,
              errorMessage: error instanceof Error ? error.message : 'Could not start assessment',
              itemCount: null,
              triggeredByUser: true,
            },
          })
          return
        }

        armAssessmentPoll({ get, set, bidId, generation, ignoreRunId })
      },

      /**
       * Re-attach to a run that is still in flight. Call this on mount.
       *
       * `stopAssessmentPolling` drops the status back to `idle` on unmount, but
       * the backend run carries on — so leaving the tab mid-assessment and
       * coming back used to show a matrix with no sign that anything was
       * happening, no refresh when it finished, and no completion notice.
       * Nothing was ever lost, but only a page reload revealed that.
       *
       * Adopts pending/running only. A terminal run is history: announcing it
       * as running would be a lie, and replaying its completion notice on every
       * remount would claim credit for work that ended before the user arrived.
       */
      resumeAssessmentPolling: async (bidId: string) => {
        // Already watching this bid — a second poll would orphan the first,
        // unreachable by `stopAssessmentPoll` and re-firing `loadCategory` on
        // every tick. A run on a DIFFERENT bid is stale by definition here.
        const current = get().assessmentRun
        if (current.status === 'running' && current.bidId === bidId) return

        // Stop and capture the generation SYNCHRONOUSLY, before the probe —
        // same contract as `startAssessment`. Deferring it until after the
        // await would make a stop landing mid-probe invisible, and we would
        // arm an interval nothing can ever clear.
        stopAssessmentPoll()
        const generation = assessmentPollGeneration
        // A `running` status left behind by another bid now has no interval
        // watching it. Left standing it would lie to this view AND lock out
        // `startAssessment`'s re-entrancy guard for the rest of the session.
        if (current.status === 'running') {
          set({ assessmentRun: createEmptyAssessmentRun() })
        }

        let run: ExtractionRunSummary | null = null
        try {
          run = await getAssessmentRun(bidId)
        } catch (error) {
          // Unlike the pre-trigger probe in `startAssessment`, a failure here
          // is safe to swallow: we are not about to trigger anything, so the
          // worst case is the user sees no progress — exactly today's
          // behaviour. A `failed` banner would blame their run for a blip.
          console.warn('Could not check for an in-flight assessment:', error)
          return
        }

        if (!run || (run.status !== 'pending' && run.status !== 'running')) return

        // A stop, or a real trigger, landed while we were probing. Either way
        // this call no longer owns the poll and must not touch shared state.
        if (assessmentPollGeneration !== generation) return

        set({
          assessmentRun: {
            status: 'running',
            runId: run.id,
            bidId,
            errorMessage: null,
            itemCount: null,
            triggeredByUser: false,
          },
        })
        armAssessmentPoll({ get, set, bidId, generation, ignoreRunId: null })
      },

      /**
       * Stop watching the run. Call this on unmount.
       *
       * Drops a `running` status back to `idle` — the backend run may well
       * continue, but we are no longer tracking it, and leaving the status at
       * `running` would both lie to the UI and lock out the re-entrancy guard
       * in `startAssessment` for the rest of the session.
       */
      stopAssessmentPolling: () => {
        stopAssessmentPoll()
        if (get().assessmentRun.status === 'running') {
          set({ assessmentRun: createEmptyAssessmentRun() })
        }
      },

      loadStaleness: async (bidId: string) => {
        try {
          // Null for a read-only member — the endpoint sits behind the write
          // guard, so a viewer 403s on what is logically a read. Absent
          // signal, not an error: callers render no prompt.
          const staleness = await getComplianceStaleness(bidId)
          set({ staleness })
        } catch (error) {
          console.warn('Failed to load compliance staleness:', error)
          set({ staleness: null })
        }
      },

      bulkPatchItems: async (
        bidId: string,
        category: ExtractionCategory,
        itemIds: string[],
        patch: Record<string, unknown> & { statusSource?: 'derived' | 'manual' },
      ) => {
        if (itemIds.length === 0) return
        // A compliance position is not a plain field: the server stamps
        // provenance and, for Unassessed, clears the verdict. Mirror that, or
        // the optimistic rows disagree with the next reload.
        const position = patch['position']
        const positionFields =
          category === 'compliance_matrix' && typeof position === 'string'
            ? positionChangeFields(position as PositionValue)
            : {}
        // Optimistic update — merge patch into each affected item.
        set((state) => ({
          categories: {
            ...state.categories,
            [category]: {
              ...state.categories[category],
              items: state.categories[category].items.map((item) => {
                const id = (item as { id?: string | null }).id
                return id && itemIds.includes(id) ? { ...item, ...patch, ...positionFields } : item
              }),
            },
          },
        }))
        const results = await Promise.allSettled(
          itemIds.map((id) =>
            api.patch(`/bids/${bidId}/extraction/${category}/${id}`, patch),
          ),
        )
        const failed = results.filter((r) => r.status === 'rejected').length
        if (failed > 0) {
          console.error(`Bulk patch failed for ${failed}/${itemIds.length} items`)
          await get().loadCategory(bidId, category)
        }
      },

      bulkDeleteItems: async (
        bidId: string,
        category: ExtractionCategory,
        itemIds: string[],
      ) => {
        if (itemIds.length === 0) return
        // Optimistic removal
        set((state) => ({
          categories: {
            ...state.categories,
            [category]: {
              ...state.categories[category],
              items: state.categories[category].items.filter((item) => {
                const id = (item as { id?: string | null }).id
                return !id || !itemIds.includes(id)
              }),
              itemCount: Math.max(
                0,
                state.categories[category].itemCount - itemIds.length,
              ),
            },
          },
        }))
        const results = await Promise.allSettled(
          itemIds.map((id) =>
            api.delete(`/bids/${bidId}/extraction/${category}/${id}`),
          ),
        )
        const failed = results.filter((r) => r.status === 'rejected').length
        if (failed > 0) {
          console.error(`Bulk delete failed for ${failed}/${itemIds.length} items`)
          await get().loadCategory(bidId, category)
        }
      },

      markCategoryViewed: (category: ExtractionCategory) => {
        set((state) => {
          if (!state.categories[category].hasUnseenCompletion) return state
          const nextCategories = {
            ...state.categories,
            [category]: {
              ...state.categories[category],
              hasUnseenCompletion: false,
            },
          }
          if (state.currentBidId) {
            saveUnseenToStorage(state.currentBidId, nextCategories)
          }
          return { categories: nextCategories }
        })
      },

      getCategoryState: (category: ExtractionCategory) => {
        return get().categories[category]
      },

      getUnseenCompletionCount: () => {
        const { categories } = get()
        let count = 0
        for (const cat of CATEGORIES) {
          if (categories[cat].hasUnseenCompletion) count++
        }
        return count
      },

      reset: () => {
        // Cancel all ongoing extractions before resetting
        for (const cat of CATEGORIES) {
          get().cancelExtraction(cat)
        }
        // A leaked interval polling a run nobody is watching is a real bug —
        // this is the unmount path.
        stopAssessmentPoll()
        set({
          categories: createEmptyCategories(),
          currentBidId: null,
          assessmentRun: createEmptyAssessmentRun(),
          staleness: null,
        })
      },
    }),
    { name: 'extraction-store' }
  )
)
